import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from './LoadingIndicator';

/**
 * Reusable Button component with multiple variants and sizes
 * Supports loading state, disabled state, and full keyboard accessibility
 * Can render as different elements using the 'as' prop (button, a, Link, etc.)
 */
const Button = forwardRef(({
  children,
  as: Component = 'button',
  variant = 'primary',
  size = 'md',
  loading = false,
  loadingText,
  disabled = false,
  className = '',
  type = 'button',
  href,
  onClick,
  onKeyDown,
  icon: Icon,
  iconPosition = 'left',
  fullWidth = false,
  ...props
}, ref) => {

  // Base styles applied to all buttons
  // Note: Using specific disabled colors instead of opacity for WCAG AA contrast compliance
  const baseStyles = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-portfolio-dark disabled:cursor-not-allowed';

  // Variant styles with WCAG AA compliant disabled states (4.5:1 contrast)
  const variants = {
    primary: 'bg-primary-600 hover:bg-primary-700 text-white focus:ring-primary-500 active:bg-primary-800 disabled:bg-gray-600 disabled:text-gray-300',
    secondary: 'bg-portfolio-gray hover:bg-portfolio-gray/80 text-white border border-white/10 focus:ring-white/20 active:bg-portfolio-gray/60 disabled:bg-gray-700 disabled:text-gray-400 disabled:border-gray-600',
    outline: 'bg-transparent hover:bg-white/5 text-white border border-white/20 hover:border-white/40 focus:ring-white/20 active:bg-white/10 disabled:text-gray-500 disabled:border-gray-600',
    ghost: 'bg-transparent hover:bg-white/5 text-white focus:ring-white/20 active:bg-white/10 disabled:text-gray-500',
    gradient: 'bg-emerald-400 hover:bg-emerald-300 text-slate-950 focus:ring-emerald-500 active:bg-emerald-500 disabled:bg-gray-600 disabled:text-gray-300',
    danger: 'bg-error-600 hover:bg-error-700 text-white focus:ring-error-500 active:bg-error-800 disabled:bg-gray-600 disabled:text-gray-300',
    success: 'bg-portfolio-green hover:bg-success-600 text-white focus:ring-success-500 active:bg-success-700 disabled:bg-gray-600 disabled:text-gray-300',
  };

  // Size styles
  const sizes = {
    sm: 'text-sm px-3 py-1.5 gap-1.5',
    md: 'text-base px-4 py-2 gap-2',
    lg: 'text-lg px-6 py-3 gap-2.5',
  };

  // Icon sizes
  const iconSizes = {
    sm: 16,
    md: 18,
    lg: 20,
  };

  // Width styles
  const widthStyle = fullWidth ? 'w-full' : '';

  // Combine all styles
  const buttonClasses = `${baseStyles} ${variants[variant]} ${sizes[size]} ${widthStyle} ${className}`;

  // Handle keyboard interactions
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!disabled && !loading && onClick) {
        onClick(e);
      }
    }
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  // Handle click
  const handleClick = (e) => {
    if (!disabled && !loading && onClick) {
      onClick(e);
    }
  };

  // Determine if we're rendering a link or button
  const isLink = Component === 'a' || href;
  const ElementToRender = isLink ? 'a' : Component;

  // Build props based on element type
  const elementProps = {
    ref,
    className: buttonClasses,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    ...props,
  };

  if (isLink) {
    // Link-specific props
    elementProps.href = href;
    elementProps.role = 'button';
    if (disabled || loading) {
      elementProps['aria-disabled'] = true;
      elementProps.tabIndex = -1;
    }
  } else {
    // Button-specific props
    elementProps.type = type;
    elementProps.disabled = disabled || loading;
    elementProps.tabIndex = disabled ? -1 : 0;
    elementProps['aria-busy'] = loading;
    elementProps['aria-disabled'] = disabled || loading;
  }

  // Spinner sizes matching button sizes
  const spinnerSizes = {
    sm: 'sm',
    md: 'sm',
    lg: 'md',
  };

  return (
    <ElementToRender {...elementProps}>
      {loading && (
        <Spinner size={spinnerSizes[size]} className="flex-shrink-0" />
      )}

      {!loading && Icon && iconPosition === 'left' && (
        <Icon size={iconSizes[size]} aria-hidden="true" />
      )}

      {loading && loadingText ? loadingText : children}

      {!loading && Icon && iconPosition === 'right' && (
        <Icon size={iconSizes[size]} aria-hidden="true" />
      )}
    </ElementToRender>
  );
});

Button.displayName = 'Button';

Button.propTypes = {
  children: PropTypes.node.isRequired,
  as: PropTypes.elementType,
  variant: PropTypes.oneOf(['primary', 'secondary', 'outline', 'ghost', 'gradient', 'danger', 'success']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  loading: PropTypes.bool,
  loadingText: PropTypes.node,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  href: PropTypes.string,
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  icon: PropTypes.elementType,
  iconPosition: PropTypes.oneOf(['left', 'right']),
  fullWidth: PropTypes.bool,
};

export default Button;
